import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import FamilyTreeCanvas from './FamilyTree/FamilyTreeCanvas';
import NodeDetailPanel from './FamilyTree/NodeDetailPanel';
import FamilyTreeLegend from './FamilyTree/FamilyTreeLegend';
import AITreeSuggestions from './FamilyTree/AITreeSuggestions';
import FamilyTreeChangeRequests from './FamilyTree/FamilyTreeChangeRequests';
import FamilyTreeVerificationModal from './FamilyTree/FamilyTreeVerificationModal';
import FamilyTreeDuplicateDetection from './FamilyTree/FamilyTreeDuplicateDetection';
import { getFamilyTree, getVerifications, GENERATION_LABELS, RELATION_COLORS } from './FamilyTree/familyTreeData';

const {
  FiUsers, FiSearch, FiFilter, FiInfo,
  FiChevronDown, FiGitBranch, FiHeart,
  FiShield, FiCheckCircle, FiClock, FiAlertCircle,
} = FiIcons;

const COUSIN_RELATIONS = ['Cousin', 'Cousin (once removed)', 'Cousin (twice removed)'];
const GENERATION_FILTERS = ['All', 'Grandparents', 'Parents', 'Core', 'Children', 'Grandchildren'];
const GEN_MAP = { All: null, Grandparents: -2, Parents: -1, Core: 0, Children: 1, Grandchildren: 2 };
const REL_FILTERS = [
  { id: 'all',    label: 'All'     },
  { id: 'direct', label: 'Direct'  },
  { id: 'cousin',  label: 'Cousins' },
];

const VERIF_STATUS_CONFIG = {
  verified:   { color: '#059669', label: 'Verified' },
  pending:    { color: '#D97706', label: 'Pending' },
  unverified: { color: '#6B7280', label: 'Unverified' },
};

const TabFamilyTree = ({ memorial }) => {
  const tree = getFamilyTree(memorial.id);
  const [verifications, setVerifications] = useState(() => getVerifications(memorial.id));

  const [selected,   setSelected]   = useState(null);
  const [anchorPos,  setAnchorPos]  = useState(null);
  const [search,     setSearch]     = useState('');
  const [genFilter,  setGenFilter]  = useState('All');
  const [relFilter,  setRelFilter]  = useState('all');
  const [showStats,  setShowStats]  = useState(false);
  const [showVerifModal, setShowVerifModal] = useState(false);

  // Compute per-node verification status
  const nodeVerifStatus = useMemo(() => {
    const map = {};
    const verifMap = {};
    verifications.forEach(v => { verifMap[v.linkId] = v; });

    tree.nodes.forEach(node => {
      const connectedLinks = tree.links.filter(
        l => l.source === node.id || l.target === node.id
      );
      if (connectedLinks.length === 0) {
        map[node.id] = 'verified';
        return;
      }
      const statuses = connectedLinks.map(l => {
        const ids = [l.source, l.target].sort();
        const linkId = `${ids[0]}-${ids[1]}-${l.type}`;
        return verifMap[linkId]?.status ?? 'unverified';
      });
      if (statuses.every(s => s === 'verified')) map[node.id] = 'verified';
      else if (statuses.some(s => s === 'pending')) map[node.id] = 'pending';
      else map[node.id] = 'unverified';
    });
    return map;
  }, [tree, verifications]);

  const visibleNodes = useMemo(() => {
    let ns = tree.nodes;
    if (GEN_MAP[genFilter] !== null)
      ns = ns.filter(n => n.generation === GEN_MAP[genFilter]);
    if (relFilter === 'cousin')
      ns = ns.filter(n => COUSIN_RELATIONS.includes(n.relation));
    else if (relFilter === 'direct')
      ns = ns.filter(n => !COUSIN_RELATIONS.includes(n.relation));
    if (search.trim()) {
      const q = search.toLowerCase();
      ns = ns.filter(n =>
        n.name.toLowerCase().includes(q) || n.relation.toLowerCase().includes(q)
      );
    }
    return ns;
  }, [tree.nodes, genFilter, relFilter, search]);

  const visibleIds   = useMemo(() => new Set(visibleNodes.map(n => n.id)), [visibleNodes]);
  const visibleLinks = useMemo(
    () => tree.links.filter(l => visibleIds.has(l.source) && visibleIds.has(l.target)),
    [tree.links, visibleIds]
  );

  const connectedNodes = useMemo(() => {
    if (!selected) return [];
    return tree.links
      .filter(l => l.source === selected.id || l.target === selected.id)
      .map(l => {
        const otherId = l.source === selected.id ? l.target : l.source;
        const node    = tree.nodes.find(n => n.id === otherId);
        return node ? { node, linkType: l.type } : null;
      })
      .filter(Boolean);
  }, [selected, tree]);

  const stats = useMemo(() => {
    const cousins = tree.nodes.filter(n => COUSIN_RELATIONS.includes(n.relation));
    const vCounts = {
      verified:   verifications.filter(v => v.status === 'verified').length,
      pending:    verifications.filter(v => v.status === 'pending').length,
      unverified: verifications.filter(v => v.status === 'unverified').length,
    };
    return {
      total:    tree.nodes.length,
      living:   tree.nodes.filter(n => !n.isDeceased).length,
      deceased: tree.nodes.filter(n => n.isDeceased).length,
      gens:     new Set(tree.nodes.map(n => n.generation)).size,
      cousins:  cousins.length,
      verif:    vCounts,
    };
  }, [tree, verifications]);

  const handleSelectNode = (node, anchor) => {
    if (selected?.id === node.id) {
      setSelected(null);
      setAnchorPos(null);
    } else {
      setSelected(node);
      setAnchorPos(anchor ?? null);
    }
  };

  const handleUpdateVerification = (linkId, updates) => {
    setVerifications(prev => {
      const existing = prev.find(v => v.linkId === linkId);
      if (existing) {
        return prev.map(v => v.linkId === linkId ? { ...v, ...updates } : v);
      }
      // Parse linkId to create new entry
      const parts = linkId.split('-');
      const type = parts[2];
      return [...prev, {
        linkId,
        sourceId: parts[0],
        targetId: parts[1],
        type,
        status: updates.status ?? 'unverified',
        ...updates,
      }];
    });
  };

  const handleRequestAllForNode = (node) => {
    // Find all unverified connections for this node and set them to pending
    const connected = tree.links.filter(l => l.source === node.id || l.target === node.id);
    connected.forEach(l => {
      const ids = [l.source, l.target].sort();
      const linkId = `${ids[0]}-${ids[1]}-${l.type}`;
      const existing = verifications.find(v => v.linkId === linkId);
      if (!existing || existing.status === 'unverified') {
        handleUpdateVerification(linkId, {
          status: 'pending',
          requestedBy: 'You',
          requestedAt: new Date().toISOString(),
          notes: 'Requested via node panel',
        });
      }
    });
  };

  return (
    <div className="space-y-6">

      {/* Header card */}
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-sm border border-brand-dark p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-accent-gold/10 border border-accent-gold/30 rounded-2xl flex items-center justify-center shadow-sm">
              <SafeIcon icon={FiGitBranch} className="w-7 h-7 text-accent-gold" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Family Tree</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-0.5">
                {stats.total} members · {stats.gens} generations
                {stats.cousins > 0 && ` · ${stats.cousins} cousin${stats.cousins !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowVerifModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-green-400 hover:text-green-700 transition-all">
              <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
              Verify
              {stats.verif.pending > 0 && (
                <span className="ml-0.5 w-4 h-4 bg-amber-500 text-white rounded-full text-[7px] font-black flex items-center justify-center">
                  {stats.verif.pending}
                </span>
              )}
            </button>
            <button onClick={() => setShowStats(v => !v)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
              <SafeIcon icon={FiInfo} className="w-3.5 h-3.5" />
              Stats
              <SafeIcon icon={FiChevronDown} className={`w-3 h-3 transition-transform ${showStats ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showStats && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8 pt-8 border-t border-brand-light">
                {[
                  { label: 'Total',       value: stats.total,    color: 'text-accent-navy' },
                  { label: 'Living',      value: stats.living,   color: 'text-green-600'   },
                  { label: 'Deceased',    value: stats.deceased, color: 'text-accent-gold'  },
                  { label: 'Generations', value: stats.gens,     color: 'text-purple-600'  },
                  { label: 'Cousins',     value: stats.cousins,  color: 'text-pink-600'    },
                ].map(s => (
                  <div key={s.label} className="text-center p-4 bg-brand-light rounded-2xl border border-brand-dark">
                    <div className={`text-3xl font-serif font-bold mb-1 ${s.color}`}>{s.value}</div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-text-muted">{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Verification mini-stats */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                {[
                  { label: 'Verified',   value: stats.verif.verified,   color: 'text-green-600',   bg: 'bg-green-50',   border: 'border-green-200' },
                  { label: 'Pending',    value: stats.verif.pending,    color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200' },
                  { label: 'Unverified', value: stats.verif.unverified, color: 'text-gray-500',    bg: 'bg-gray-50',    border: 'border-gray-200' },
                ].map(s => (
                  <div key={s.label} className={`text-center p-3 ${s.bg} rounded-2xl border ${s.border}`}>
                    <div className={`text-2xl font-serif font-bold mb-1 ${s.color}`}>{s.value}</div>
                    <div className="text-[8px] font-black uppercase tracking-widest text-text-muted">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <FamilyTreeDuplicateDetection
        nodes={tree.nodes}
        onReview={node => handleSelectNode(node, null)}
      />

      {/* AI Suggestions */}
      <AITreeSuggestions treeNodes={tree.nodes} memorial={memorial} />

      {/* Change request availability */}
      <FamilyTreeChangeRequests />

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or relationship…"
              className="w-full pl-11 pr-5 py-3.5 bg-white border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm font-medium transition-colors" />
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
            {GENERATION_FILTERS.map(f => (
              <button key={f} onClick={() => setGenFilter(f)}
                className={`px-4 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${
                  genFilter === f
                    ? 'bg-accent-navy border-accent-navy text-white'
                    : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          {REL_FILTERS.map(f => {
            const isActive = relFilter === f.id;
            const isCousin = f.id === 'cousin';
            return (
              <button key={f.id} onClick={() => setRelFilter(f.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                  isActive
                    ? isCousin ? 'text-white border-transparent' : 'bg-accent-navy border-accent-navy text-white'
                    : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                }`}
                style={isActive && isCousin
                  ? { background: RELATION_COLORS.cousin, borderColor: RELATION_COLORS.cousin }
                  : {}}>
                {isCousin && (
                  <span className="w-3 h-3 rounded-full inline-flex items-center justify-center text-[7px] font-black"
                    style={{ background: isActive ? 'white' : RELATION_COLORS.cousin, color: isActive ? RELATION_COLORS.cousin : 'white' }}>
                    C
                  </span>
                )}
                {f.label}
                {isCousin && stats.cousins > 0 && (
                  <span className="ml-0.5 text-[8px] opacity-80">({stats.cousins})</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tree canvas + anchored detail panel */}
      <div className="relative">
        <FamilyTreeCanvas
          nodes={visibleNodes}
          links={visibleLinks}
          selectedNode={selected}
          onSelectNode={handleSelectNode}
          nodeVerifStatus={nodeVerifStatus}
        />
        {selected && (
          <NodeDetailPanel
            node={selected}
            connectedNodes={connectedNodes}
            anchorPos={anchorPos}
            onClose={() => { setSelected(null); setAnchorPos(null); }}
            verifications={verifications}
            onRequestVerification={handleRequestAllForNode}
            memorialId={memorial?.id}
          />
        )}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-[28px] border border-brand-dark p-6 shadow-sm">
        <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-4 flex items-center gap-2">
          <SafeIcon icon={FiFilter} className="w-3 h-3" /> Legend
        </p>
        <FamilyTreeLegend />
        {/* Verification legend */}
        <div className="mt-4 pt-4 border-t border-brand-light">
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-3 flex items-center gap-2">
            <SafeIcon icon={FiShield} className="w-3 h-3" /> Verification
          </p>
          <div className="flex flex-wrap gap-4">
            {[
              { status: 'verified',   label: 'Verified',   icon: FiCheckCircle, color: '#059669' },
              { status: 'pending',    label: 'Pending',    icon: FiClock,       color: '#D97706' },
              { status: 'unverified', label: 'Unverified', icon: FiAlertCircle, color: '#6B7280' },
            ].map(v => {
              const Icon = v.icon;
              return (
                <div key={v.status} className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest"
                    style={{ background: `${v.color}15`, color: v.color }}>
                    <Icon className="w-2.5 h-2.5" /> {v.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Member list */}
      <div className="bg-white rounded-[40px] border border-brand-dark p-8 shadow-sm">
        <h4 className="text-xl font-serif text-accent-navy mb-6 flex items-center gap-3">
          <SafeIcon icon={FiUsers} className="text-accent-gold" />
          All Family Members
          <span className="text-sm font-sans font-normal text-text-muted">({visibleNodes.length})</span>
        </h4>

        {(relFilter === 'all' || relFilter === 'cousin') && stats.cousins > 0 && (
          <div className="mb-5 flex items-start gap-3 p-4 rounded-2xl border text-[11px] leading-relaxed"
            style={{ background: '#FDF2F8', borderColor: `${RELATION_COLORS.cousin}30`, color: '#831843' }}>
            <SafeIcon icon={FiHeart} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: RELATION_COLORS.cousin }} />
            <span>
              <strong>Cousin branch</strong> — nodes with a pink dashed ring and <strong>C</strong> badge are cousins.
              They share grandparents with the core generation and are connected by pink dashed lines.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visibleNodes.map(node => {
            const isCousin = COUSIN_RELATIONS.includes(node.relation);
            const isActive = selected?.id === node.id;
            const vStatus = nodeVerifStatus[node.id] ?? 'unverified';
            const vCfg = VERIF_STATUS_CONFIG[vStatus];
            const VerifIcon = vCfg.icon;
            return (
              <motion.button key={node.id}
                onClick={() => handleSelectNode(node, null)}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                className="flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all"
                style={isActive
                  ? { borderColor: isCousin ? RELATION_COLORS.cousin : '#C9A84C',
                      background:   isCousin ? '#FDF2F8' : 'rgba(201,168,76,0.05)' }
                  : { borderColor: isCousin ? `${RELATION_COLORS.cousin}40` : undefined,
                      background:   isCousin ? '#FFF8FC' : undefined }}>
                <div className="relative shrink-0">
                  <img
                    src={node.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(node.name)}&background=1E3A5F&color=C9A84C&bold=true&size=80`}
                    alt={node.name}
                    className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"
                  />
                  {node.isDeceased && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-accent-gold rounded-full text-[8px] flex items-center justify-center">🕯</div>
                  )}
                  {isCousin && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[7px] font-black text-white"
                      style={{ background: RELATION_COLORS.cousin }}>C</div>
                  )}
                  {/* Verification badge on list avatar */}
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full border border-white flex items-center justify-center"
                    style={{ background: vCfg.color }}>
                    <VerifIcon className="w-2 h-2 text-white" />
                  </div>
                </div>
                <div className="min-w-0 flex-grow">
                  <p className="font-bold text-accent-navy text-sm truncate">{node.name}</p>
                  <p className="text-[9px] font-black uppercase tracking-widest mt-0.5"
                    style={{ color: isCousin ? RELATION_COLORS.cousin : '#C9A84C' }}>
                    {node.relation}
                  </p>
                  <p className="text-[9px] text-text-muted font-mono mt-0.5">
                    {node.birthYear}{node.deathYear ? ` – ${node.deathYear}` : ''}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest shrink-0"
                  style={{ background: `${vCfg.color}15`, color: vCfg.color }}>
                  <VerifIcon className="w-2 h-2" />
                  {vCfg.label}
                </span>
                {isActive && (
                  <div className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: isCousin ? RELATION_COLORS.cousin : '#C9A84C' }} />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Verification Modal */}
      <FamilyTreeVerificationModal
        isOpen={showVerifModal}
        onClose={() => setShowVerifModal(false)}
        nodes={tree.nodes}
        links={tree.links}
        verifications={verifications}
        onUpdateVerification={handleUpdateVerification}
      />
    </div>
  );
};

export default TabFamilyTree;